import type GroupedFileAccess from '../../interfaces/GroupedFileAccess';

/**
 * Whether the current user may elevate a share to Editor / change editor-related rules,
 * before applying API `allowEdit` relaxation for direct shares.
 *
 * Mirrors legacy FileShareForm logic: owners; or non-owners who performed all reshares in the group.
 */
export function computeCanElevateToEditor(
  areAllSelectedFilesMine: boolean,
  allOriginalAreEditor: boolean,
  allPerformedByMe: boolean,
): boolean {
  if (areAllSelectedFilesMine) {
    return areAllSelectedFilesMine || allOriginalAreEditor;
  }
  if (allPerformedByMe) {
    return true;
  }
  return false;
}

/**
 * Whether the per-row expiration (calendar) control should be enabled for a direct-share row.
 * When `allowEdit` is true and every selected file is writable, direct shares are editable
 * regardless of `performedByUserId`; otherwise the legacy elevation rules apply.
 */
export function isDirectShareExpirationPickerEnabled(
  isEditAllowed: boolean,
  allSelectedFilesWritable: boolean,
  canElevateToEditor: boolean,
  groupedCanEdit: GroupedFileAccess['canEdit'],
): boolean {
  if (isEditAllowed && allSelectedFilesWritable) {
    return true;
  }
  return !(!canElevateToEditor && groupedCanEdit === true);
}

/**
 * Whether the Editor option in the permission Select should be disabled for a direct-share row.
 */
export function isDirectShareEditorOptionDisabled(
  isEditAllowed: boolean,
  allSelectedFilesWritable: boolean,
  canElevateToEditor: boolean,
): boolean {
  if (isEditAllowed && allSelectedFilesWritable) {
    return false;
  }
  return !canElevateToEditor;
}

/** i18n keys explaining why the Editor permission option is greyed out. */
export type EditorOptionDisabledReasonKey =
  'common.editorDisabledByPolicy' | 'common.editorDisabledCannotElevate';

/**
 * Why Editor is disabled for new-share or direct-share Select options.
 * Returns an i18n key, or null when Editor is available.
 */
export function getEditorOptionDisabledReason(args: {
  /** New share: false when upstream / policy blocks canEdit. */
  newShareCanEditAllowed?: boolean;
  /** Existing access row: pass when `isDirectShareEditorOptionDisabled` is true. */
  cannotElevateReceivedShare?: boolean;
}): EditorOptionDisabledReasonKey | null {
  if (args.newShareCanEditAllowed === false) {
    return 'common.editorDisabledByPolicy';
  }
  if (args.cannotElevateReceivedShare === true) {
    return 'common.editorDisabledCannotElevate';
  }
  return null;
}
